import { isCamaraActiva } from '../../utils/camaraStatus';

// Programa de fidelidad: el nivel sale de la cantidad de cámaras activas y
// los beneficios dependen de si la empresa tiene obras en CABA / AMBA
// (jornadas audiovisuales en obra) o fuera (servicio 100% remoto).

export const NIVELES = [
  {
    id: 'plata',
    nombre: 'Plata',
    min: 1,
    max: 3,
    rango: '1 a 3 cámaras',
    ritmo: '1 pieza por año',
    beneficios: {
      amba: [
        { texto: '1 jornada audiovisual por año: vamos a grabarla' },
        { texto: 'Entrevista + publicación en colaboración, 1 por año' },
        { texto: '1 edición profesional por año + timelapses y fotos ilimitados' },
        { texto: 'Jornadas adicionales: a cotizar aparte', excluido: true },
      ],
      remoto: [
        { texto: 'Acompañamiento virtual: soporte y seguimiento a distancia' },
        { texto: '1 edición profesional por año (por ejemplo, el cierre de obra)' },
        { texto: 'Timelapses y fotos ilimitados desde la plataforma' },
        { texto: 'Jornadas, entrevistas y colabs: solo CABA / AMBA', excluido: true },
      ],
    },
  },
  {
    id: 'oro',
    nombre: 'Oro',
    min: 4,
    max: 9,
    rango: '4 a 9 cámaras',
    ritmo: '1 pieza cada 3 meses',
    beneficios: {
      amba: [
        { texto: '2 jornadas audiovisuales por año en hitos clave' },
        { texto: 'Entrevista + publicación en colaboración, 2 por año' },
        { texto: 'Edición profesional trimestral y reunión de seguimiento' },
        { texto: 'Servicios beta: no incluidos', excluido: true },
      ],
      remoto: [
        { texto: 'Acompañamiento 100% virtual, sin visitas a obra' },
        { texto: 'Edición profesional trimestral con el material de las cámaras' },
        { texto: 'Reunión trimestral de seguimiento' },
        { texto: 'Entrevistas, colabs y beta: no incluidos', excluido: true },
      ],
    },
  },
  {
    id: 'diamante',
    nombre: 'Diamante',
    min: 10,
    max: Infinity,
    rango: '10 cámaras o más',
    ritmo: '1 pieza por mes',
    beneficios: {
      amba: [
        { texto: '3 jornadas audiovisuales por año, una cada 4 meses' },
        { texto: 'Entrevista + publicación en colaboración en cada jornada' },
        { texto: 'Edición profesional mensual y acompañamiento virtual permanente' },
        { texto: 'Acceso anticipado a servicios beta (visión IA, reportes, Obra 3D)' },
      ],
      remoto: [
        { texto: 'Acompañamiento 100% virtual, sin visitas a obra' },
        { texto: 'Edición profesional mensual con el material de las cámaras' },
        { texto: 'Reunión mensual de seguimiento' },
        { texto: 'Acceso anticipado a servicios beta' },
      ],
    },
  },
];

// Activa = ni Terminado ni Suspendida (mismo criterio que la facturación).
export const esCamaraActiva = isCamaraActiva;

export const nivelPorCamaras = (cantidad) =>
  NIVELES.find((n) => cantidad >= n.min && cantidad <= n.max) || NIVELES[0];

export const siguienteNivel = (nivel) => {
  const i = NIVELES.findIndex((n) => n.id === nivel.id);
  return i >= 0 && i < NIVELES.length - 1 ? NIVELES[i + 1] : null;
};

// AMBA = CABA + conurbano (de Zárate/Campana a La Plata/Brandsen y hasta Luján/Cañuelas).
// El borde este sigue la costa del Río de la Plata, así Colonia (Uruguay) queda afuera.
const AMBA_POLIGONO = [
  [-34.05, -59.20], [-34.05, -58.85], [-34.40, -58.50], [-34.55, -58.38],
  [-34.62, -58.33], [-34.75, -58.15], [-34.85, -57.85], [-35.05, -57.80],
  [-35.25, -58.25], [-35.20, -58.85], [-34.95, -59.10], [-34.65, -59.30],
  [-34.30, -59.35],
];

export const parseCoordenadas = (c) => {
  if (!c) return null;
  let lat;
  let lng;
  if (typeof c === 'string') {
    [lat, lng] = c.split(',').map((v) => parseFloat(v));
  } else if (typeof c === 'object') {
    lat = parseFloat(c.lat ?? c.latitude ?? c._latitude);
    lng = parseFloat(c.lng ?? c.longitude ?? c._longitude);
  }
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

export const estaEnAmba = (coords) => {
  const p = parseCoordenadas(coords);
  if (!p) return false;
  let dentro = false;
  for (let i = 0, j = AMBA_POLIGONO.length - 1; i < AMBA_POLIGONO.length; j = i++) {
    const [yi, xi] = AMBA_POLIGONO[i];
    const [yj, xj] = AMBA_POLIGONO[j];
    if ((yi > p.lat) !== (yj > p.lat) && p.lng < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi) {
      dentro = !dentro;
    }
  }
  return dentro;
};

// Con al menos una cámara activa en CABA / AMBA la empresa accede a las jornadas en obra.
export const calcularFidelidad = (camaras = []) => {
  const activas = camaras.filter(esCamaraActiva);
  const cantidad = activas.length;
  const nivel = nivelPorCamaras(cantidad);
  const proximo = siguienteNivel(nivel);
  const zona = activas.some((c) => estaEnAmba(c.coordinates)) ? 'amba' : 'remoto';
  const faltan = proximo ? Math.max(0, proximo.min - cantidad) : 0;
  const progreso = proximo ? Math.min(1, cantidad / proximo.min) : 1;
  return { cantidad, nivel, proximo, zona, faltan, progreso };
};
